import { useState } from "react";
import { Download, FileText } from "lucide-react";
import { useTranslation } from "../../../context/TranslationContext";
import { useAuth } from "../../../context/AuthContext";
import { useApp } from "../../../context/AppContext";
import { ModalWrapper, ModalHeader, ModalBody, ModalFooter, InfoBox, FormField, FormSelect, Button } from "../../common";
import toast from "react-hot-toast";
import { loadExcelJS } from "../../../utils/lazyImports";
import { getAllAvailabilityReportAggregated } from "../../../lib/api/report";

interface AggregatedAvailabilityReportModalProps {
  onClose: () => void;
}

interface AggregatedReportRow {
  date: string;
  datepart: string;
  users: string;
}

export function AggregatedAvailabilityReportModal({ onClose }: AggregatedAvailabilityReportModalProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { currentUser } = useApp();
  const [isLoading, setIsLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear().toString());

  const exportToExcel = async () => {
    if (!token || !currentUser) {
      toast.error(t("auth.authenticationRequired"));
      return;
    }

    setIsLoading(true);
    try {
      // Dynamically load ExcelJS only when needed
      const ExcelJS = await loadExcelJS();
      
      const data = await getAllAvailabilityReportAggregated(token, currentUser.site, year);

      if (!data || data.length === 0) {
        toast.error(t("reports.noDataAvailable"));
        setIsLoading(false);
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Aggregated Availability");

      const headers = [t("common.date"), "Period", t("reports.unavailableUsers")];
      worksheet.addRow(headers);

      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE6E6E6' }
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      });

      data.sort((a: AggregatedReportRow, b: AggregatedReportRow) => {
        const dateCompare = a.date.localeCompare(b.date);
        if (dateCompare !== 0) return dateCompare;
        return a.datepart.localeCompare(b.datepart);
      });

      // ensure date is recognised as date in Excel by converting to Date object
      data.forEach((row: AggregatedReportRow) => {
        worksheet.addRow([
          new Date(row.date),
          row.datepart,
          row.users
        ]);
      });

      worksheet.getColumn(1).width = 15;
      worksheet.getColumn(1).numFmt = 'yyyy-mm-dd';
      worksheet.getColumn(2).width = 10;
      worksheet.getColumn(3).width = 30;

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          row.eachCell((cell) => {
            cell.border = {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' }
            };
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
          });
        }
      });

      const fileName = `Aggregated_Availability_Report_${year}_${new Date().toISOString().split('T')[0]}.xlsx`;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);

      toast.success(t("reports.exportSuccess"));
    } catch (error) {
      console.error("Failed to export aggregated availability:", error);
      toast.error(t("reports.exportFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => (currentYear + i - 2).toString());

  return (
    <ModalWrapper onClose={onClose} size="md">
      <ModalHeader
        icon={<FileText />}
        title={t("reports.aggregatedAvailability")}
        onClose={onClose}
      />

      <ModalBody>
        <InfoBox variant="info" icon={<FileText />} title={t("reports.aggregatedAvailabilityDescription")}>
          {t("reports.aggregatedAvailabilityInstructions")}
        </InfoBox>

        <FormField label={t("common.year")}>
          <FormSelect
            value={year}
            onChange={(e) => setYear(e.target.value)}
            options={years.map((y) => ({ value: y, label: y }))}
          />
        </FormField>
      </ModalBody>

      <ModalFooter alignment="right">
        <Button
          variant="secondary"
          onClick={onClose}
        >
          {t("common.cancel")}
        </Button>
        <Button
          variant="primary"
          onClick={exportToExcel}
          disabled={isLoading}
          loading={isLoading}
          icon={<Download />}
        >
          {t("reports.exportToExcel")}
        </Button>
      </ModalFooter>
    </ModalWrapper>
  );
}
